import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import * as atlas from 'azure-maps-control';
import { AuthenticationType } from 'azure-maps-control';
import { DomSanitizer } from '@angular/platform-browser';
import { ProjectService } from '../../core/services/project.service';
import { EmployeeCacheService } from '../../core/services/employee-cache.service';
import { Project } from '../../shared/models/projects/project.model';
import { EmployeePermissionType } from '../../shared/models/employee/employee-permissions-type.model';

declare var $: any;

@Component({
  selector: 'app-project-search',
  templateUrl: './project-search.component.html',
  styleUrls: ['./project-search.component.css']
})
export class ProjectSearchComponent implements OnInit, OnDestroy {

  collapsed: boolean = true;
  mainWindowVisible: boolean = false;

  // Getters so these update once the permissions finish loading
  get canViewAlberIQ() {
    return this.employeeCacheService.checkPermissions([EmployeePermissionType.CanViewAlberIQ]);
  }

  get CanViewProjectSiteConditions() {
    return this.employeeCacheService.checkPermissions([EmployeePermissionType.CanViewProjectSiteConditions]);
  }

  get canViewPPRList() {
    return this.employeeCacheService.checkPermissions([EmployeePermissionType.CanViewPPRList]);
  }

  subscriptions: Subscription = new Subscription();
  key: string = '4WDQBD6oO6ENn5vpiJg1kMZrQzwtmMW3gOxupLXWeEW6FLexrRaFJQQJ99CEACYeBjFCIJONAAAgAZMP15xy';
  map: any;
  popup: any;
  datasource: any;
  searchLayer: any;
  clusterBubbleLayer: any;
  //projects: any[] = [];
  projects: Project[] = [];
  isLoadingProjects: boolean = true;
  project: any;
  features: any;
  shapes: any;

  private searchHoverHandler!: EventListener;
  private searchUnhoverHandler!: EventListener;
  private popupClickHandler!: EventListener;
  private popupHoverHandler!: EventListener;
  private popupUnHoverHandler!: EventListener;
  private clusterClickHandler!: EventListener;
  private clusterHoverHandler!: EventListener;
  private clusterUnHoverHandler!: EventListener;

  baseUrl: string = '';
  hoverProjectId: string = '';
  searchText: string = '';
  iframeUrl: any;
  selectedShape: any;
  keepPopupOpen: boolean = false;

  selectedId: string = '';
  hoverShape: any;

  activeWindow: string = '';

  constructor(
    //private userService: UserService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private projectService: ProjectService,
    private ref: ChangeDetectorRef,
    private employeeCacheService: EmployeeCacheService
  ) {
    this.baseUrl = location.origin;
  }

  ngOnInit() {
    //Initialize a map instance.

    this.map = new atlas.Map('myMap', {
      center: [-97, 39],
      zoom: 3,
      view: 'Auto',
      language: 'Auto',
      style: "grayscale_light",

      authOptions: {
        authType: AuthenticationType.subscriptionKey,
        subscriptionKey: this.key
      }
    });

    //Wait until the map resources are ready.
    this.popup = new atlas.Popup();

    //Wait until the map resources are ready.
    this.map.events.add('ready', () => {

      //Add the zoom control to the map.
      this.map.controls.add(new atlas.control.ZoomControl(), {
        position: 'top-right'
      });

      this.datasource = new atlas.source.DataSource('datasource', {
        //Tell the data source to cluster point data.
        cluster: true,

        //The radius in pixels to cluster points together.
        clusterRadius: 45,

        //The maximium zoom level in which clustering occurs.
        //If you zoom in more than this, all points are rendered as symbols.
        clusterMaxZoom: 15
      });
      this.map.sources.add(this.datasource);

      //Create a bubble layer for rendering clustered data points.
      this.clusterBubbleLayer = new atlas.layer.BubbleLayer(this.datasource, 'clusterBubbleLayer', {
        createIndicators: true, // to enable bubble layer a11y feature
        //Scale the size of the clustered bubble based on the number of points in the cluster.
        radius: [
          'step',
          ['get', 'point_count'],
          20,         //Default of 20 pixel radius.
          100, 30,    //If point_count >= 100, radius is 30 pixels.
          750, 40     //If point_count >= 750, radius is 40 pixels.
        ],

        //Change the color of the cluster based on the value on the point_cluster property of the cluster.
        color: [
          'step',
          ['get', 'point_count'],
          'rgba(200,16,46,0.8)',            //Default to Alberici Red.
          100, 'rgba(255,255,0,0.8)',     //If the point_count >= 100, color is yellow.
          750, 'rgba(255,0,0,0.8)'        //If the point_count >= 100, color is red.
        ],
        strokeWidth: 0,
        filter: ['has', 'point_count'] //Only rendered data points which have a point_count property, which clusters do.
      });

      this.clusterClickHandler = (event: any) => {
        console.log('Cluster click detected!', event);
        if (event.shapes && event.shapes.length > 0 && event.shapes[0].properties.cluster) {
          //Get the clustered point from the event.
          var cluster = event.shapes[0];

          //Get the cluster expansion zoom level. This is the zoom level at which the cluster starts to break apart.
          this.datasource.getClusterExpansionZoom(cluster.properties.cluster_id).then((zoom: any) => {

            //Update the map camera to be centered over the cluster. 
            this.map.setCamera({
              center: cluster.geometry.coordinates,
              zoom: zoom,
              type: 'ease',
              duration: 200
            });
          });
        }
      };

      this.clusterHoverHandler = (event: any) => {
        console.log('Cluster hover detected!', event);
        this.map.getCanvasContainer().style.cursor = 'pointer';
      };

      this.clusterUnHoverHandler = (event: any) => {
        console.log('Cluster hover detected!', event);
        this.map.getCanvasContainer().style.cursor = 'grab';
      };

      //Add a click event to the layer so we can zoom in when a user clicks a cluster.
      this.map.events.add('click', this.clusterBubbleLayer, this.clusterClickHandler);

      //Add mouse events to change the mouse cursor when hovering over a cluster.
      this.map.events.add('mouseenter', this.clusterBubbleLayer, this.clusterHoverHandler);
      this.map.events.add('mouseleave', this.clusterBubbleLayer, this.clusterUnHoverHandler);

      //Create a bubble layer for rendering locations.
      this.searchLayer = new atlas.layer.SymbolLayer(this.datasource, 'layer', {
        filter: ['!', ['has', 'point_count']], //Filter out clustered points from this layer.
        iconOptions: {
          image: ['get', 'icon'],
          anchor: 'center',
          allowOverlap: true,
          ignorePlacement: true
        }
      });

      this.searchHoverHandler = (event: any) => {
        console.log('Mouse over detected!', event);
        if (event.shapes && event.shapes.length > 0) {
          this.map.getCanvasContainer().style.cursor = 'pointer';
          this.searchText = '';
          this.hoverProjectId = event.shapes[0].data.properties.id;
          this.hoverShape = event.shapes[0];
          this.showHoverPopup();
        }
      };

      this.searchUnhoverHandler = (event: any) => {
        setTimeout(() => {
          if (!this.keepPopupOpen) {
            this.map.getCanvasContainer().style.cursor = '';
            this.popup.close()
          }
        }, 2000);
      };

      //Add mouse events to change the mouse cursor when hovering over a cluster.
      this.map.events.add('mouseenter', this.searchLayer, this.searchHoverHandler);
      this.map.events.add('mouseleave', this.searchLayer, this.searchUnhoverHandler);

      this.popupHoverHandler = (event: any) => {
        this.keepPopupOpen = true;
      };

      this.popupUnHoverHandler = (event: any) => {
        this.popup.close()
      };

      this.popupClickHandler = (event: any) => {
        var coords = this.popup.getOptions().position;
        const clickedShape = this.datasource.getShapes().find((shape: any) => shape.data.geometry.coordinates[0] === coords[0] && shape.data.geometry.coordinates[1] === coords[1]);
        if (clickedShape) {
          //const id = clickedShape.data.properties.id;
          this.project = this.projects.find((project) => project.id === this.hoverProjectId);
          this.collapsed = true;
          this.toggleDrawer();
          this.searchText = '';

          // Change icon to a different built-in or custom image
          const selectedId = clickedShape.data.id;

          // Reset previously selected point
          if (this.selectedId) {
            const prevShape = this.datasource.getShapeById(this.selectedId);
            if (prevShape) {
              prevShape.setProperties({ ...prevShape.getProperties(), icon: 'pin-blue' });
            }
          }

          // Set new selected point
          this.selectedId = selectedId;
          clickedShape.setProperties({ ...clickedShape.getProperties(), icon: 'marker-red' });
        }
        this.popup.close();
      };

      //Add click event to popup to open side drawer
      this.popup.getPopupContainer().addEventListener('click', this.popupClickHandler);

      //Add mouse events to popup.
      this.popup.getPopupContainer().addEventListener('mouseenter', this.popupHoverHandler);
      this.popup.getPopupContainer().addEventListener('mouseleave', this.popupUnHoverHandler);

      //Add the clusterBubbleLayer and two additional layers to the map.
      this.map.layers.add([
        this.clusterBubbleLayer,

        //Create a symbol layer to render the count of locations in a cluster.
        new atlas.layer.SymbolLayer(this.datasource, 'countsLayer', {
          iconOptions: {
            image: 'none' //Hide the icon image.
          },
          textOptions: {
            textField: ['get', 'point_count_abbreviated'],
            offset: [0, 0.4]
          }
        }),

        this.searchLayer
      ]);

      // Get projects so we can create features
      // Waits for the employee load so the x-user-id header is set
      this.employeeCacheService.loaded$
        .pipe(
          take(1),
          switchMap(() => this.projectService.getProjects())
        )
        .subscribe({
          next: (projects) => {
            console.log(projects);
            this.projects = projects;
            this.projects.forEach(project => {
              this.datasource.add(new atlas.data.Feature(new atlas.data.Point([project.location.longitude, project.location.latitude]), {
                id: project.id,
                name: project.displayName,
                company: project.company,
                addressLine: project.location.address + ', ' + project.location.city + ', ' + project.location.state + ' ' + project.location.zip,
                imageUrl: "https://saworkmediaprodcus01.blob.core.windows.net/project-photos/IMG_8080-Edit-Edit-1140x760.jpg",
                icon: 'pin-blue'
              }));
            })
          },
          error: (err) => {
            //this.toastService.show('Error loading data.', ToastMode.Danger);
            console.log(err);
          }
        }).add(() => {
          this.isLoadingProjects = false;
        });
    });
  }

  getProject(id: string) {
    return this.projects.find((project) => project.id = id);
  }

  zoomToProject(project: any) {
    console.log(project);

    //zoom camera to shape
    this.map.setCamera({
      center: [project.location.longitude, project.location.latitude], //shape.getCoordinates(),
      zoom: 17
    });

    this.project = project;
    this.searchText = '';
    this.ref.detectChanges();
    this.popup.close();
  }

  onHover(project: any) {
    //this.showPopup(project);
    this.hoverProjectId = project.id;
    this.showHoverPopup();
  }

  showHoverPopup() {
    let hoveredProject: { image: string; name: string; addressLine: string; } = {
      image: "14_2273_OKC 1 PP 2nd Floor Remodel 1_medium.jpg",
      name: "",
      addressLine: ""
    };

    if (this.hoverShape && this.hoverShape.data.properties && this.hoverShape.data.properties.id) {
      hoveredProject.image = this.hoverShape.data.properties.imageUrl;
      hoveredProject.name = this.hoverShape.data.properties.name;
      hoveredProject.addressLine = this.hoverShape.data.properties.addressLine;
    }
    else {
      var project = this.projects.find((project) => project.id === this.hoverProjectId);
      if (project) {
        hoveredProject.image = project.images?.[0] ?? "";
        hoveredProject.name = project.displayName ?? "";
        hoveredProject.addressLine = (project.location.address + ', ' + project.location.city + ', ' + project.location.state + ' ' + project.location.zip);
      }
    }

    //const html = ['<div style="width: 300px; height: 100px; padding: 0; margin: 0; border-radius: 8px; cursor: pointer; display: flex;">'];
    ////Add image section
    //html.push('<div style="float: left; width: 100px; box-sizing: border-box; padding: 10px;">');
    //html.push('<img src="', hoveredProject.image, '" width="80" />');
    //html.push('</div>');

    ////Add a content section for the popup.
    //html.push('<div style="background-color: #fff; width: 200px; height: 100px; padding-top: 7px; color: #000; font-size: 14px; white-space: pre-wrap; overflow-wrap: break-word; float: right;">');
    //html.push('<b>', hoveredProject.name, '</b><br>', hoveredProject.addressLine);
    //html.push('</div>');

    //html.push('</div>');

    const html = ['<div style="width: 300px; height: 105px; padding: 0; margin: 0; border-radius: 8px; cursor: pointer;">'];
    //Add image section
    html.push('<div style="float: left; width: 100px; height: 50px; box-sizing: border-box; padding: 7px 0px 0px 10px; display: flex;">');
    html.push('<img src="', hoveredProject.image, '" width="80" />');
    html.push('</div>');

    //Add project title section for the popup.
    html.push('<div style="background-color: #fff; width: 200px; height: 40px; padding-top: 7px; color: #000; font-size: 14px; white-space: pre-wrap; overflow-wrap: break-word; float: right; display: flex;">');
    html.push('<b>', hoveredProject.name, '</b>');
    html.push('</div>');

    //Add project location section for the popup.
    html.push('<div style="background-color: #fff; width: 300px; height: 40px; padding: 10px; color: #000; font-size: 14px; white-space: pre-wrap; overflow-wrap: break-word; float: right;">');
    html.push(hoveredProject.addressLine);
    html.push('</div>');

    html.push('</div>');

    this.popup.setOptions({
      position: [this.hoverShape.data.geometry.coordinates[0], this.hoverShape.data.geometry.coordinates[1]],
      content: html.join(''),
      style: 'pointer',
      closeButton: false,
      showPointer: true
    });
    this.popup.open(this.map);
  }

  toggleDrawer() {
    this.collapsed = !this.collapsed;
    if (this.collapsed == true)
      this.mainWindowVisible = false;
  }

  showPPRWindow() {
    this.activeWindow = 'ppr';
    this.mainWindowVisible = true;
    const url = this.baseUrl + '/embedreviews/' + this.project.id;
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  showTrueLookWindow() {
    this.activeWindow = 'truelook';
    this.mainWindowVisible = true;
    const url = (this.project?.cameraUrl) ? this.project?.cameraUrl : 'https://app.truelook.cloud/link?code=a7TYY_UYQgeOBan2eTIXtQ&type=sharing_link';
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  showAnalyticsWindow() {
    this.activeWindow = 'powerbi';
    this.mainWindowVisible = true;
    const url = 'https://app.powerbi.com/reportEmbed?reportId=40e4006c-929f-4d00-997c-2650b6e216ed&autoAuth=true&ctid=2d0703cb-a705-4aef-99af-fd1244540e2b&filter=project%2Fproject_number%20eq%20%27' + this.project.id + '%27';
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  showAlbericiAIWindow() {
    this.activeWindow = 'alberiq';
    this.mainWindowVisible = true;
    //const url = 'https://m365.cloud.microsoft/chat/?titleId=T_6406ae30-6440-ba51-970d-c80395560b9e'
    const url = this.baseUrl + '/embedalberiq';
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  closeContentWindow() {
    this.activeWindow = '';
    this.mainWindowVisible = false;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.map) {
      this.map.dispose();
    }
  }
}
